import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { polyfill } from "interweave-ssr";
import Image from "next/image";
import Link from "next/link";
import SocialMediaCta from "@/components/sections/SocialMediaCta";
import NewsletterCta from "@/components/sections/NewsletterCta";
import { useRouter } from "next/router";
import Head from "next/head";
import { Favicon } from "@/components/common/Favicon";
import OgImage from "@/components/common/OgImage";


function formatDate(dateString) {
  const [day, month, year] = dateString.split("/");
  return `${year}-${month}-${day}`;
}

export default function Recursos({ postData }) {
  polyfill();
  const router = useRouter();

  const [processedContent, setProcessedContent] = useState("");


  useEffect(() => {
    if (postData?.post?.content) {
      const container = document.createElement("div");
      container.innerHTML = postData.post.content;

      const elements = Array.from(container.children);
      let applyIndent = false;

      elements.forEach((el) => {
        if (el.tagName === "H3") {
          applyIndent = true;
          el.style.paddingLeft = "20px";
          el.classList.add("indent-h3");
        } else if (["H2", "H1"].includes(el.tagName)) {
          applyIndent = false;
        } else if (applyIndent && el.tagName === "P") {
          el.style.paddingLeft = "20px";
          el.classList.add("indent-p");
        } else {
          el.style.paddingLeft = "0";
          el.classList.remove("indent-p");
        }
      });

      setProcessedContent(container.innerHTML);
    }
  }, [postData]);

  if (postData.length === 0) {
    if (process.browser) {
      router.push("/blog");
    }
    return null;
  }

  const formattedDate = formatDate(postData.post.date);

  const metadata = {
    url: `https://www.actionis-consulting.com/blog/${postData.post.slug}`,
    title: `${postData.post.name}`,
    description: postData.post.metadescription,
    keywords: postData.post.keywords,
    image: postData.post.image,
    twitterCard: "summary_large_image",
    twitterSite: "@actionis",
    ogType: "article",
    appName: "Actionis",
    published_time: formattedDate,
  };

  const schema = {
    "@context": "http://schema.org",
    "@type": "BlogPosting",
    headline: `${metadata.title} | Actionis Consulting` || "Título por defecto",
    description: metadata.description || "Descripción por defecto",
    image: metadata.image || "https://via.placeholder.com/150",
    author: {
      "@type": "Person",
      name: postData?.post?.author || "Autor por defecto",
      url: "https://www.actionis-consulting.com/#equipo",
    },
    datePublished: formattedDate
      ? `${formattedDate}T00:00:00Z`
      : "2024-01-01T00:00:00Z",
    dateModified: formattedDate
      ? `${formattedDate}T00:00:00Z`
      : "2024-01-01T00:00:00Z",
    publisher: {
      "@type": "Organization",
      name: metadata.appName || "Actionis Consulting",
      url: "https://www.actionis-consulting.com",
      logo: {
        "@type": "ImageObject",
        url: "https://www.actionis-consulting.com/logo.png",
      },
    },
  };

  return (
    <>
      <Head>
        <title>{metadata.title} | Recursos</title>
        <Favicon />
        <OgImage image={metadata.image} title={metadata.title} />
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />
        <meta name="twitter:card" content={metadata.twitterCard} />
        <meta name="twitter:site" content={metadata.twitterSite} />
        <meta name="twitter:title" content={metadata.title} />
        <meta name="twitter:description" content={metadata.description} />
        <meta name="twitter:image" content={metadata.image} />
        <meta
          property="article:published_time"
          content={metadata.published_time}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              schema,
              (key, value) => {
                if (value === undefined || value === null) return undefined;
                return value;
              },
              2
            ),
          }}
        />
      </Head>
      {postData && (
        <div className="z-20 flex items-start justify-center w-full max-w-xl !mx-auto md:max-w-5xl 2xl:max-w-7xl mt-[120px] extra-posts-styles">
          <div className="w-full h-full px-5 pb-10 bg-white md:py-10">
            <div
              className="grid max-w-2xl grid-cols-1 mx-auto my-4 gap-x-6 gap-y-10 lg:mx-0 lg:max-w-none lg:gap-x-8 xl:col-span-2"
            >
              <div className="w-full mx-auto">
                <nav
                  className="flex pb-5 pt-2 md:py-5 !overflow-hidden"
                  aria-label="Breadcrumb"
                >
                  <ul
                    role
                    className="flex items-center space-x-4 !list-none"
                  >
                    <li>
                      <div>
                        <Link
                          href="/"
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <svg
                            className="flex-shrink-0 w-5 h-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M9.293 2.293a1 1 0 011.414 0l7 7A1 1 0 0117 11h-1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-3a1 1 0 00-1-1H9a1 1 0 00-1 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-6H3a1 1 0 01-.707-1.707l7-7z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </Link>
                      </div>
                    </li>
                    <li className="overflow-hidden">
                      <Link
                        href={"/recursos"}
                        className="flex items-center"
                      >
                        <svg
                          className="flex-shrink-0 w-5 h-5 text-gray-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <p
                          className="inline-block ml-4 text-xs font-medium text-gray-500 truncate md:text-sm "
                          aria-current="page"
                        >
                          Recursos
                        </p>
                      </Link>
                    </li>
                  </ul>
                </nav>
                <Image
                  width={1000}
                  height={1000}
                  className="object-cover w-full h-[400px] md:h-[600px] rounded-2xl"
                  src={postData.post.image}
                  alt={postData.post.name}
                />
                <div className="flex flex-col md:flex-row justify-between mt-2 text-sm gap-2 text-gray-700 text-center md:text-left">
                  <p>{postData.post.author}</p>
                  <p>Fecha de Publicación: {postData.post.date}</p>
                </div>
                <h1 className="uppercase text-shadow-yellow text-3xl lg:text-4xl font-extrabold max-w-prose mx-auto text-center my-10 max-w-screen-lg">
                  {postData.post.name}
                </h1>
                <div
                  dangerouslySetInnerHTML={{ __html: processedContent }}
                  className="flex flex-col text-base !text-inherit list-disc max-w-screen-lg mx-auto post-content"
                ></div>
                <div className="pt-10 text-center">
                  <Link
                    href="/recursos"
                    className="font-bold bg-blue-light px-8 py-3 text-white rounded-xl"
                  >
                    Volver a Recursos
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <SocialMediaCta />
      <NewsletterCta bg="rgb(253, 190, 15, 0.9)" />
    </>
  );
}

Recursos.propTypes = {
  postData: PropTypes.any,
  error: PropTypes.any,
};

const getPost = async ({ slug }) => {
  const resPost = await fetch(
    `${process.env.NEXT_PUBLIC_API}/api/v1/posts/${slug}`
  );
  const post = await resPost.json();
  return post;
};

export async function getServerSideProps(context) {
  const { slug } = context.query;
  const post = await getPost({ slug });

  if (!post || Object.keys(post).length === 0 || post.message === "Post not found.") {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      postData: post,
    },
  };
}
